import { FileInput, Image, ActionIcon, Group, Text, Stack, LoadingOverlay, Box } from '@mantine/core';
import { IconUpload, IconX, IconPhoto } from '@tabler/icons-react';
import { useState } from 'react';
import api from '@/services/api-service';

interface ImageUploadPreviewProps {
    label: string;
    value?: string | null;
    uploadKey: string;
    onChange: (url: string) => void;
    placeholder?: string;
    height?: number;
    uploadUrl?: string;
    onRemove?: () => void;
}

export default function ImageUploadPreview({
    label,
    value,
    uploadKey,
    onChange,
    placeholder = 'Upload image',
    height = 120,
    uploadUrl = '/store/upload',
    onRemove
}: ImageUploadPreviewProps) {
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (file: File | null) => {
        if (!file) {
            onChange('');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        formData.append('key', uploadKey);

        try {
            const res = await api.upload<{ url: string, key: string }>(uploadUrl, formData);
            if (res.data?.url) {
                onChange(res.data.url);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div>
            <label>
                {label}
            </label>

            <div>
                <LoadingOverlay visible={uploading} zIndex={10} overlayProps={{ radius: "sm", blur: 2 }} />
                {value ? (
                    <div className="solid-box-border w-fit">
                        <Image
                            src={value}
                            h={height}
                            w="100%"
                            fit="contain"
                            alt={label}
                        />
                        <div>
                            <FileInput
                                accept="image/*"
                                style={{ display: 'none' }}
                                id={`replace-${uploadKey}`}
                                onChange={handleUpload}
                            />
                            <label htmlFor={`replace-${uploadKey}`}>
                                <ActionIcon
                                    variant="filled"
                                    color="white"
                                    className="text-black hover:bg-gray-100 cursor-pointer"
                                    size="sm"
                                    component="div"
                                >
                                    <IconUpload size={18} />
                                </ActionIcon>
                            </label>
                        </div>
                        <ActionIcon
                            variant="filled"
                            color="red"
                            size="sm"
                            onClick={() => {
                                if (onRemove) {
                                    onRemove();
                                } else {
                                    onChange('');
                                }
                            }}>
                            <IconX size={18} />
                        </ActionIcon>
                    </div>
                ) : (
                    <div className="border border-dashed rounded-md bg-gray-50 hover:bg-gray-100 transition-colors" style={{ height }}>
                        <FileInput
                            accept="image/*"
                            style={{ display: 'none' }}
                            id={`upload-${uploadKey}`}
                            onChange={handleUpload}
                        />
                        <label
                            htmlFor={`upload-${uploadKey}`}
                            className="flex flex-col items-center justify-center h-full cursor-pointer p-4"
                        >
                            <IconPhoto size={24} className="text-gray-400 mb-2" />
                            <Text size="sm" c="dimmed">{placeholder}</Text>
                        </label>
                    </div>
                )}
            </div>
        </div>
    );
}
